/**
 * --------------------------------------------------------------------
 *  Production build — Facets with unified SKU Availability (OR logic)
 * --------------------------------------------------------------------
 * What this script enables
 * - A single Shopify facet param for availability:
 *     filter.p.m.cariuma-v2.sku_availability
 *   with values like: onhand_W070, preorder_M090 (OR within this one filter)
 * - Persistent UI state in the URL:
 *     availability_type=onhand[,preorder]
 * - Desktop + mobile availability toggles (display-only inputs; no name=)
 * - Virtual active-facet pills for availability (desktop + mobile) that:
 *     • use the theme’s close SVG
 *     • survive removing other (real) pills
 *     • support “Clear all” even when only virtual filters are active
 * - Native Availability (In stock) auto-on rules:
 *     • If ANY facet is selected and NO virtual availability is active,
 *       we auto-apply native availability (filter.v.availability=1).
 *     • We do NOT auto-select the virtual toggles (onhand/preorder).
 *     • If virtual availability is active, tokens are generated per size(s).
 * - Selected-count UI:
 *     • Horizontal layout: “N selected” badge in the custom block header
 *     • Vertical layout:   “(N)” counter inside the Summary label (like other facets)
 *
 * Safe-guard behavior (facet absent or no preorder tokens)
 * - If the combined SKU Availability facet is not present in the DOM:
 *     • All other filters continue to work normally
 *     • Availability UI is hidden and behaves as a no-op
 *     • Virtual pills are not injected
 * - If there are NO preorder_* values among inputs named filter.p.m.cariuma-v2.sku_availability
 *   within this facet-filters-form, the virtual Availability UI is disabled/hidden as well.
 *
 * Navigation model
 * - Full page reload for maximum theme compatibility
 * - BFCache-safe: re-syncs on history back/forward
 * --------------------------------------------------------------------
 */

(() => {
  'use strict';

  /** -------------------------- Constants --------------------------- */

  /** Combined availability facet param (Shopify). */
  const SKU_AVAIL_PARAM = 'filter.p.m.cariuma-v2.sku_availability';
  /** Native availability facet param + value (In stock). */
  const NAT_AVAIL_PARAM = 'filter.v.availability';
  const NAT_AVAIL_VALUE = '1';
  /** Size facet param name (adjust if your theme uses another). */
  const SIZE_PARAM_NAME = 'filter.v.option.size';
  /** Helper/URL param for virtual availability state. */
  const AVAIL_HELPER_NAME = 'availability_type';
  /** Selector for availability toggles in the UI. */
  const AVAIL_TOGGLE_SELECTOR = 'input[type="checkbox"][data-availability-type]';
  /** Labels for virtual pills. */
  const AVAIL_PILL_LABEL = { onhand: 'Ready to ship', preorder: 'Pre-Order' };

  /** IDs we’ll use/expect per layout */
  const IDS = {
    horizontal: 'Details-availability-custom',
    vertical:   'Details-availability-custom-vertical',
    drawer:     'Details-Mobile-availability-custom'
  };

  /** Map storefront Size labels -> SKU suffix used in combined facet tokens. */
  const sizeToSkuMap = {
    'Women 5 / Men 3.5': 'W050',
    'Women 5.5 / Men 4': 'W055',
    'Women 6 / Men 4.5': 'W060',
    'Women 6.5 / Men 5': 'W065',
    'Women 7 / Men 5.5': 'W070',
    'Women 7.5 / Men 6': 'W075',
    'Women 8 / Men 6.5': 'W080',
    'Women 8.5 / Men 7': 'W085',
    'Women 9 / Men 7.5': 'W090',
    'Women 9.5 / Men 8': 'M080',
    'Women 10 / Men 8.5': 'M085',
    'Women 10.5 / Men 9': 'M090',
    'Women 11 / Men 9.5': 'M095',
    'Women 11.5 / Men 10': 'M100',
    'Women 12 / Men 10.5': 'M105',
    'Women 12.5 / Men 11': 'M110',
    'Women 13 / Men 11.5': 'M115',
    'Women 13.5 / Men 12': 'M120',
    'Women 14 / Men 12.5': 'M125',
    'Women 14.5 / Men 13': 'M130'
  };

  /** Build a normalized lookup so minor label differences still map. */
  const normalizedSizeMap = buildNormalizedSizeMap(sizeToSkuMap);

  function normalizeSizeLabel(s) {
    return String(s || '')
      .replace(/\u00A0/g, ' ')           // NBSP → space
      .replace(/\s*\(.*?\)\s*/g, ' ')    // drop parenthetical suffixes e.g., (EU 40)
      .replace(/\s*\/\s*/g, ' / ')       // normalize slashes " / "
      .replace(/\s+/g, ' ')              // collapse spaces
      .trim()
      .toLowerCase();
  }

  function buildNormalizedSizeMap(map) {
    const out = Object.create(null);
    Object.keys(map).forEach((k) => {
      out[normalizeSizeLabel(k)] = map[k];
    });
    return out;
  }

  /** Robust match for availability token prefixes (handles '-', '_' and case). */
  function hasAvailPrefix(val, type) {
    if (!val) return false;
    const s = String(val).toLowerCase();
    if (type === 'onhand')  return s.startsWith('onhand_')  || s.startsWith('on-hand_')  || s.startsWith('on_hand_');
    if (type === 'preorder')return s.startsWith('preorder_')|| s.startsWith('pre-order_')|| s.startsWith('pre_order_');
    return false;
  }

  /** --------------------------- Utilities -------------------------- */

  /** Parse CSV availability string ("onhand,preorder") into a clean list. */
  const parseAvailabilityCSV = (s) =>
    (s || '')
      .split(',')
      .map(v => v.trim())
      .filter(v => v === 'onhand' || v === 'preorder');

  /** Read active availability from the current (or last) URL. */
  function getActiveAvailabilityFromURL() {
    const usp = new URLSearchParams(
      FacetFiltersForm.searchParamsPrev || window.location.search.slice(1)
    );
    return parseAvailabilityCSV(usp.get(AVAIL_HELPER_NAME));
  }

  /** Return true if FormData includes any FILTER (other than our combined/helper/native). */
  function hasAnyFacetSelectedInFD(fd) {
    for (const [name] of fd.entries()) {
      if (!name || !name.startsWith('filter.')) continue;
      if (name === SKU_AVAIL_PARAM) continue; // we add our own later
      if (name === NAT_AVAIL_PARAM) continue; // we may add later
      return true; // found a real facet (size, color, material, etc.)
    }
    return false;
  }

  /** Determine if a root contains ANY preorder_* inputs for the combined facet. */
  function rootHasAnyPreorderTokens(root) {
    const nodes = (root || document).querySelectorAll(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`);
    for (const n of nodes) {
      const v = n && n.value || '';
      if (hasAvailPrefix(v, 'preorder')) return true;
    }
    return false;
  }

  /** Document-level helper (used by static pills injector). */
  function documentHasAnyPreorderTokens() {
    return rootHasAnyPreorderTokens(document);
  }

  /** ----------------------- Facet Filters Form --------------------- */

  class FacetFiltersForm extends HTMLElement {
    constructor() {
      super();
      this.onInput = this.onInput.bind(this);
      this.onClick = this.onClick.bind(this);
      this.form = this.querySelector('form') || this.closest('form');

      // Layout detection (per theme markup)
      this.layout = this.detectLayout();

      // Does THIS section render the combined SKU facet?
      this.hasSkuFacet = !!this.querySelector(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`);

      // Is there at least one preorder_* value under THIS facet-filters-form?
      this.hasAnyPreorderTokens = rootHasAnyPreorderTokens(this);

      // Enable virtual Availability only if combined facet exists AND preorder tokens are present here.
      this.virtualAvailabilityEnabled = this.hasSkuFacet && this.hasAnyPreorderTokens;
      if (this.layout === 'drawer' &&
          !this.virtualAvailabilityEnabled &&
          documentHasAnyPreorderTokens()) {
        this.virtualAvailabilityEnabled = true;
      }

      // Build/ensure the correct Availability UI for this layout.
      this.ensureCustomAvailabilityUI();

      // Establish initial availability state (only if virtual is enabled).
      const usp = new URLSearchParams(window.location.search);
      const haveSkuAvailInURL = usp.has(SKU_AVAIL_PARAM);
      const availabilityFromURL = parseAvailabilityCSV(usp.get(AVAIL_HELPER_NAME));

      let active = [];
      if (this.virtualAvailabilityEnabled) {
        if (availabilityFromURL.length) {
          active = availabilityFromURL;
        } else if (haveSkuAvailInURL) {
          const prefixes = new Set();
          this.querySelectorAll(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]:checked`)
            .forEach(cb => {
              const v = cb.value || '';
              if (hasAvailPrefix(v, 'onhand')) prefixes.add('onhand');
              if (hasAvailPrefix(v, 'preorder')) prefixes.add('preorder');
            });
          active = Array.from(prefixes);
        }
      }

      this.activeAvailability = active;
      this.syncAvailabilityTogglesUI();
      this.updateAvailabilitySelectedCount();
      this.syncNativeAvailabilityCheckbox(); // auto-sync native checkbox on load
    }

    connectedCallback() {
      if (this.form) {
        // Use 'change' for stable checkbox state; capture to catch clicks on labels/SVGs.
        this.form.addEventListener('change', this.onInput, true);
        this.form.addEventListener('click', this.onClick, true);
      }

      if (this.virtualAvailabilityEnabled) {
        // Ensure hidden helper exists and is in sync.
        this.setHelperAvailabilityParam();

        // Sync combined facet checkboxes for current state (sizes + availability).
        this.syncCombinedAvailabilityCheckboxes();

        // Wire "Reset" links when present (horizontal + drawer only)
        this.querySelectorAll('#ResetAvailability, #ResetAvailabilityMobile').forEach(a => {
          a.addEventListener('click', (ev) => {
            ev.preventDefault();
            this.activeAvailability = [];
            this.setHelperAvailabilityParam();
            this.syncCombinedAvailabilityCheckboxes();
            this.syncAvailabilityTogglesUI();
            this.updateAvailabilitySelectedCount();
            this.navigateWithCurrentFormState(ev);
          });
        });

        // Inject virtual availability pills & one-time global delegation.
        FacetFiltersForm.injectVirtualAvailabilityPills();
        FacetFiltersForm.bindVirtualAvailabilityPillClicks();

        // BFCache safety
        window.addEventListener('pageshow', () => {
          FacetFiltersForm.injectVirtualAvailabilityPills();
          this.syncAvailabilityTogglesUI?.();
          this.updateAvailabilitySelectedCount();
          this.syncNativeAvailabilityCheckbox?.();
        });
      }
    }

    /** --------------------------- Layout & UI --------------------------- */

    detectLayout() {
      // Drawer/mobile lives in its own form
      if (this.form && (this.form.id === 'FacetFiltersFormMobile' || this.form.classList.contains('mobile-facets'))) {
        return 'drawer';
      }
      // Vertical desktop
      if (this.querySelector('.facets__disclosure-vertical') || this.form?.classList.contains('facets__form-vertical')) {
        return 'vertical';
      }
      // Default desktop horizontal
      return 'horizontal';
    }

    /** Detects/creates the proper Availability UI for the current layout. */
    ensureCustomAvailabilityUI() {
      // If THIS instance can’t run virtual availability, only hide its own block.
      if (!this.virtualAvailabilityEnabled) {
        const localDesktop = this.querySelector('#Details-availability-custom');
        if (localDesktop) localDesktop.style.display = 'none';
        return;
      }

      // Drawer/mobile: never create the desktop element here.
      if (this.layout === 'drawer') {
        const mobileDetails =
          this.querySelector('#Details-Mobile-availability-custom') ||
          document.getElementById('Details-Mobile-availability-custom');
        if (mobileDetails) mobileDetails.style.display = '';
        // Do not create #Details-availability-custom in the drawer.
        return;
      }

      // Desktop (vertical or horizontal) — create/use the desktop element.
      const desktopWrapper =
        this.querySelector('#FacetsWrapperDesktop') ||
        this.querySelector('.facets-container') ||
        this;

      let details = desktopWrapper.querySelector('#Details-availability-custom');
      if (!details) {
        details = document.createElement('details');
        details.id = 'Details-availability-custom';
        const beforeNative = desktopWrapper.querySelector('[id^="Details-filter.v.availability-"]');
        desktopWrapper.insertBefore(details, beforeNative || null);
      }

      if (this.layout === 'vertical') {
        details.className = 'facets__disclosure-vertical js-filter';
        details.setAttribute('data-index', 'availability');
        details.innerHTML = `
          <summary class="facets__summary focus-offset" aria-label="Availability (0 selected)">
            <div>
              <span class="facets__summary-label field__label">
                Availability
                <span class="facets__selected hidden">(0)</span>
              </span>
              <svg aria-hidden="true" focusable="false" class="icon icon-caret" width="10px" height="6px" viewBox="0 0 10 6" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <g id="Header-5-(New-Images)" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <g id="navArrow" transform="translate(-86.000000, -83.000000)" fill="#000">
                    <path d="M94.1258896,87.6058178 L92.5161335,87.6058178 L92.5151822,82.4831104 L87.3941822,82.4838665 L87.3941822,80.8741104 L94.1258896,80.8741104 L94.1258896,87.6058178 Z" id="Combined-Shape" transform="translate(90.760036, 84.239964) rotate(135.000000) translate(-90.760036, -84.239964) "></path>
                  </g>
                </g>
              </svg>
            </div>
          </summary>
          <div id="Facet-availability-custom" class="parent-display facets__display-vertical">
            <fieldset class="facets-wrap parent-wrap facets-wrap-vertical">
              <legend class="visually-hidden">Availability</legend>
              <ul class="facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled" role="list">
                <li class="list-menu__item facets__item">
                  <label for="Filter-availability-onhand" class="facets__label facet-checkbox">
                    <input type="checkbox" id="Filter-availability-onhand" data-availability-type="onhand" autocomplete="off">
                    <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect></svg>
                    <svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" fill="none"></path>
                    </svg>
                    <span class="facet-checkbox__text" aria-hidden="true"><span class="facet-checkbox__text-label">Ready to ship</span></span>
                    <span class="visually-hidden">Ready to ship</span>
                  </label>
                </li>
                <li class="list-menu__item facets__item">
                  <label for="Filter-availability-preorder" class="facets__label facet-checkbox">
                    <input type="checkbox" id="Filter-availability-preorder" data-availability-type="preorder" autocomplete="off">
                    <svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect></svg>
                    <svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" fill="none"></path>
                    </svg>
                    <span class="facet-checkbox__text" aria-hidden="true"><span class="facet-checkbox__text-label">Pre-Order</span></span>
                    <span class="visually-hidden">Pre-Order</span>
                  </label>
                </li>
              </ul>
            </fieldset>
          </div>
        `;
      } else {
        // Horizontal desktop: just ensure it’s visible if your theme renders it
        details.className = 'disclosure-has-popup facets__disclosure js-filter';
        details.style.display = '';
      }
    }

    /** --------------------------- Events --------------------------- */

    onClick(e) {
      // Let 'change' handle availability toggles (stable checked state).
      const inputEl = e.target.closest(AVAIL_TOGGLE_SELECTOR);
      if (inputEl) return;
    }

    onInput(e) {
      const inputEl = e.target.closest(AVAIL_TOGGLE_SELECTOR);
      if (inputEl) {
        if (!this.virtualAvailabilityEnabled) return; // disabled → ignore toggles
        this.handleAvailabilityToggle(inputEl, e);
        return;
      }

      // For other facets: recompute tokens & navigate.
      this.syncCombinedAvailabilityCheckboxes();
      this.navigateWithCurrentFormState(e);
    }

    /** ----------------------- State + Syncing ---------------------- */

    setHelperAvailabilityParam() {
      if (!this.virtualAvailabilityEnabled) return;
      let helper = this.querySelector(`input[name="${AVAIL_HELPER_NAME}"]`);
      if (!helper) {
        helper = document.createElement('input');
        helper.type = 'hidden';
        helper.name = AVAIL_HELPER_NAME;
        this.form && this.form.appendChild(helper);
      }
      helper.value = this.activeAvailability.join(',');
    }

    getSelectedSizes() {
      const boxes = this.querySelectorAll(`input[name="${CSS.escape(SIZE_PARAM_NAME)}"]:checked`);
      return Array.from(boxes).map(cb => cb.value).filter(Boolean);
    }

    /**
     * Build combined availability tokens to submit:
     * - If sizes selected → tokens per size map (e.g., onhand_M090).
     * - If no sizes → all tokens for selected availability prefixes (scoped to THIS form).
     */
    computeCombinedTokens() {
      const tokens = new Set();
      if (!this.hasSkuFacet) return tokens;
      if (!this.activeAvailability.length) return tokens;

      const sizes = this.getSelectedSizes();

      if (sizes.length > 0) {
        sizes.forEach((sizeLabel) => {
          const sku =
            sizeToSkuMap[sizeLabel] ||
            normalizedSizeMap[normalizeSizeLabel(sizeLabel)];
          if (!sku) return;
          this.activeAvailability.forEach((type) => tokens.add(`${type}_${sku}`));
        });
        return tokens;
      }

      // No sizes selected → include ALL tokens for the selected availability prefixes
      this.querySelectorAll(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`).forEach((cb) => {
        const val = cb.value || '';
        this.activeAvailability.forEach((type) => {
          if (hasAvailPrefix(val, type)) tokens.add(val);
        });
      });

      return tokens;
    }

    /** Check only the desired combined availability inputs; uncheck everything else. */
    syncCombinedAvailabilityCheckboxes() {
      const all = this.querySelectorAll(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`);
      if (!all.length) return;

      all.forEach(cb => { cb.checked = false; });

      const tokens = this.computeCombinedTokens();
      if (!tokens.size) return;

      tokens.forEach(token => {
        this.querySelectorAll(
          `input[name="${CSS.escape(SKU_AVAIL_PARAM)}"][value="${CSS.escape(token)}"]`
        ).forEach(cb => { cb.checked = true; });
      });
    }

    /** Mirror activeAvailability -> all toggles (any layout). */
    syncAvailabilityTogglesUI() {
      const want = new Set(this.activeAvailability);
      this.querySelectorAll(AVAIL_TOGGLE_SELECTOR).forEach(cb => {
        const type = cb.getAttribute('data-availability-type');
        cb.checked = want.has(type);
      });
    }

    /**
     * Keep the native "In stock" checkbox in sync with URL or current state.
     * (Purely visual aid if the native input is visible; otherwise harmless.)
     */
    syncNativeAvailabilityCheckbox() {
      const usp = new URLSearchParams(window.location.search);
      const nativeInURL =
        usp.getAll(NAT_AVAIL_PARAM).includes(NAT_AVAIL_VALUE) ||
        usp.get(NAT_AVAIL_PARAM) === NAT_AVAIL_VALUE;
      const shouldBeChecked = nativeInURL || (this.activeAvailability.length > 0);

      this.querySelectorAll(`input[name="${CSS.escape(NAT_AVAIL_PARAM)}"][value="${CSS.escape(NAT_AVAIL_VALUE)}"]`)
        .forEach(cb => { cb.checked = shouldBeChecked; });
    }

    /** Toggle a single availability type and submit. */
    handleAvailabilityToggle(inputEl, e) {
      const type = inputEl.getAttribute('data-availability-type');
      if (!type) return;

      const next = new Set(this.activeAvailability);
      inputEl.checked ? next.add(type) : next.delete(type);
      this.activeAvailability = Array.from(next);

      this.setHelperAvailabilityParam();
      this.syncCombinedAvailabilityCheckboxes();
      this.syncAvailabilityTogglesUI();
      this.updateAvailabilitySelectedCount();
      this.navigateWithCurrentFormState(e);
    }

    /**
     * Build the querystring from the current form state and navigate.
     * Skips raw combined inputs + helper (adds both explicitly at the end).
     * Ensures native availability (In stock) when:
     *   - any facet is selected AND no virtual availability selected, OR
     *   - any virtual availability is selected.
     */
    navigateWithCurrentFormState(event) {
      if (!this.form) return;
      if (event && typeof event.preventDefault === 'function') event.preventDefault();

      const fd = new FormData();

      // Include all checked inputs/selects EXCEPT:
      //  - combined param (we add computed tokens)
      //  - helper (we add a clean value once)
      this.form.querySelectorAll('input, select').forEach(el => {
        const { name } = el;
        if (!name) return;
        if (name === SKU_AVAIL_PARAM) return;
        if (name === AVAIL_HELPER_NAME) return;

        if (el.tagName === 'SELECT') {
          if (el.value) fd.append(name, el.value);
          return;
        }
        if (el.type === 'checkbox' || el.type === 'radio') {
          if (el.checked) fd.append(name, el.value);
          return;
        }
        if (el.type !== 'file' && el.value) {
          fd.append(name, el.value);
        }
      });

      // Add combined availability tokens (OR semantics) if virtual availability is active.
      this.computeCombinedTokens().forEach(token => fd.append(SKU_AVAIL_PARAM, token));

      // Persist virtual availability state in URL ONLY if virtual UI is enabled.
      if (this.virtualAvailabilityEnabled && this.activeAvailability.length) {
        fd.append(AVAIL_HELPER_NAME, this.activeAvailability.join(','));
      }

      // Ensure native availability:
      const anyFacetSelected = hasAnyFacetSelectedInFD(fd);
      const needNative =
        this.activeAvailability.length > 0 || (anyFacetSelected && this.activeAvailability.length === 0);

      if (needNative) {
        const hasNativeInFD = Array.from(fd.keys()).some(k => k === NAT_AVAIL_PARAM);
        if (!hasNativeInFD) fd.append(NAT_AVAIL_PARAM, NAT_AVAIL_VALUE);
      }

      const params = new URLSearchParams(fd).toString();
      const base = window.location.pathname.replace(/\/+$/, '');
      window.location.assign(`${base}?${params}`);
    }

    /** Remove a single virtual availability and submit. */
    removeAvailability(type, event) {
      const set = new Set(this.activeAvailability || []);
      set.delete(type);
      this.activeAvailability = Array.from(set);

      this.setHelperAvailabilityParam?.();
      this.syncCombinedAvailabilityCheckboxes?.();
      this.syncAvailabilityTogglesUI?.();
      this.updateAvailabilitySelectedCount();

      this.navigateWithCurrentFormState(event);
    }

    /** Update the “selected” counters per layout (vertical shows "(N)" in Summary). */
    updateAvailabilitySelectedCount() {
      const count = Array.isArray(this.activeAvailability) ? this.activeAvailability.length : 0;
      if (this.layout === 'vertical') {
        const summary = this.querySelector('#Details-availability-custom .facets__summary');
        const counter = summary?.querySelector('.facets__selected');
        if (counter) {
          counter.textContent = `(${count})`;
          counter.classList.toggle('hidden', count === 0);
          summary.setAttribute('aria-label', `Availability (${count} selected)`);
        }
      } else {
        const desktopCounter = this.querySelector('#Facet-availability-custom .facets__selected') ||
                              document.querySelector('#Facet-availability-custom .facets__selected');
        if (desktopCounter) desktopCounter.textContent = `${count} selected`;
      }
    }


    /** -------------------- Virtual Pills (static) ------------------- */

    static buildAvailabilityPill(type) {
      const label = AVAIL_PILL_LABEL[type] || type;
      const pill = document.createElement('facet-remove');
      pill.setAttribute('data-availability-pill', type);
      pill.innerHTML = `
        <a href="#"
           class="active-facets__button active-facets__button--light"
           data-remove-availability="${type}">
          <span class="active-facets__button-inner button button--tertiary">
            ${label}
            <svg aria-hidden="true" focusable="false" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
              <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <span class="visually-hidden">Remove filter</span>
          </span>
        </a>`;
      return pill;
    }

    static ensureActiveFacetsContainer(selector) {
      let container = document.querySelector(selector);
      if (container) return container;

      const host =
        document.getElementById('FacetsWrapperDesktop') ||
        document.querySelector('.facets-container') ||
        document.body;

      container = document.createElement('div');
      container.className = selector.replace(/^\./, '');
      host.appendChild(container);
      return container;
    }

    /**
     * Inject (or update) virtual availability pills.
     * Bail out entirely when the page contains no preorder_* tokens at all.
     */
    static injectVirtualAvailabilityPills() {
      // If combined facet doesn't exist at all, or no preorder tokens anywhere → don't inject.
      if (!document.querySelector(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`)) return;
      if (!documentHasAnyPreorderTokens()) return;

      const active = getActiveAvailabilityFromURL();

      // --- DESKTOP (supports vertical + horizontal) ---
      const desktop = document.querySelector('.active-facets-desktop');
      if (desktop) {
        // Remove previously injected availability pills in desktop container
        desktop.querySelectorAll('[data-availability-pill]').forEach(el => el.remove());

        if (active.length) {
          const verticalHeader = desktop.querySelector('.active-facets-vertical-filter');

          const insertDesktop = (node) => {
            if (verticalHeader) {
              // VERTICAL: pills must be siblings AFTER the header (like native pills)
              verticalHeader.insertAdjacentElement('afterend', node);
            } else {
              // HORIZONTAL: place before a direct-child Clear-all if present; else append
              const directClear =
                Array.from(desktop.children).find(
                  (c) => c.classList && c.classList.contains('active-facets__button-wrapper')
                ) || null;
              directClear ? desktop.insertBefore(node, directClear) : desktop.appendChild(node);
            }
          };

          active.forEach(type => insertDesktop(FacetFiltersForm.buildAvailabilityPill(type)));

          // Only ensure Clear-all on non-vertical; vertical already has its own inside the header
          if (!verticalHeader) FacetFiltersForm.ensureClearAllButton(desktop);
        }
      }

      // --- MOBILE (drawer) — unchanged logic (inject before Clear-all if direct child) ---
      const mobile = document.querySelector('.active-facets-mobile');
      if (mobile) {
        mobile.querySelectorAll('[data-availability-pill]').forEach(el => el.remove());

        if (active.length) {
          const directClear =
            Array.from(mobile.children).find(
              (c) => c.classList && c.classList.contains('active-facets__button-wrapper')
            ) || null;

          const insertMobile = (node) => {
            directClear ? mobile.insertBefore(node, directClear) : mobile.appendChild(node);
          };

          active.forEach(type => insertMobile(FacetFiltersForm.buildAvailabilityPill(type)));
          FacetFiltersForm.ensureClearAllButton(mobile);
        }
      }
    }

    static bindVirtualAvailabilityPillClicks() {
      if (FacetFiltersForm._availabilityPillsBound) return;
      FacetFiltersForm._availabilityPillsBound = true;

      document.addEventListener('click', (e) => {
        const a = e.target.closest('[data-remove-availability]');
        if (!a) return;
        e.preventDefault();

        document.querySelectorAll('facet-filters-form').forEach((instance) => {
          if (typeof instance.removeAvailability === 'function') {
            instance.removeAvailability(a.getAttribute('data-remove-availability'), e);
          }
        });
      }, true);
    }

    static ensureClearAllButton(containerOrSelector) {
      const container = typeof containerOrSelector === 'string'
        ? document.querySelector(containerOrSelector)
        : containerOrSelector;
      if (!container) return;

      // Skip vertical variant: theme renders its own Clear-all inside .active-facets-vertical-filter
      if (container.querySelector('.active-facets-vertical-filter')) return;

      if (container.querySelector('.active-facets__button-remove')) return;

      let wrapper = Array.from(container.children).find(
        (c) => c.classList && c.classList.contains('active-facets__button-wrapper')
      );
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'active-facets__button-wrapper';
        container.appendChild(wrapper);
      }

      const clear = document.createElement('facet-remove');
      clear.innerHTML = `<a href="${window.location.pathname}" class="active-facets__button-remove underlined-link">Clear all</a>`;
      wrapper.appendChild(clear);
    }
  }

  customElements.define('facet-filters-form', FacetFiltersForm);

  /** ----------------------- Active Facet Removal ------------------- */

  class FacetRemove extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
    }
    connectedCallback() { this.addEventListener('click', this.onClick); }
    disconnectedCallback() { this.removeEventListener('click', this.onClick); }

    onClick(event) {
      const link = event.target.closest('a');
      if (!link) return;

      // Virtual availability pill? handled elsewhere
      if (link.hasAttribute('data-remove-availability')) return;

      // Combined facet present?
      const skuFacetExists = !!document.querySelector(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`);

      // ---- CLEAR ALL ----
      const isClearAll =
        link.classList.contains('active-facets__button-remove') ||
        !!link.closest('.active-facets__button-wrapper');

      if (isClearAll) {
        event.preventDefault();

        document.querySelectorAll('facet-filters-form').forEach((inst) => {
          inst.activeAvailability = [];
          inst.setHelperAvailabilityParam?.();
          inst.syncCombinedAvailabilityCheckboxes?.();
          inst.syncAvailabilityTogglesUI?.();
          inst.updateAvailabilitySelectedCount?.();
        });

        const baseHref = link.getAttribute('href');
        const pathOnly = (baseHref && baseHref !== '#'
          ? new URL(baseHref, window.location.origin).pathname
          : window.location.pathname);
        window.location.assign(pathOnly);
        return;
      }

      // ---- REAL pill (preserve virtual availability + native In stock) ----
      if (!skuFacetExists) return;

      try {
        const instance = document.querySelector('facet-filters-form');
        const activeAvail = (instance && Array.isArray(instance.activeAvailability))
          ? instance.activeAvailability.slice()
          : [];

        if (!activeAvail.length) return; // nothing to preserve

        event.preventDefault();

        const url = new URL(link.href, window.location.origin);
        const params = url.searchParams;

        // Persist virtual availability state and native availability
        params.set(AVAIL_HELPER_NAME, activeAvail.join(','));
        params.set(NAT_AVAIL_PARAM, NAT_AVAIL_VALUE);

        // Rebuild combined availability tokens for the target URL
        params.delete(SKU_AVAIL_PARAM);

        const sizeVals = params.getAll(SIZE_PARAM_NAME);
        const appendToken = (token) => {
          const existing = params.getAll(SKU_AVAIL_PARAM);
          if (!existing.includes(token)) params.append(SKU_AVAIL_PARAM, token);
        };

        if (sizeVals.length) {
          sizeVals.forEach((label) => {
            const sku =
              sizeToSkuMap[label] ||
              normalizedSizeMap[normalizeSizeLabel(label)];
            if (!sku) return;
            activeAvail.forEach((type) => appendToken(`${type}_${sku}`));
          });
        } else {
          document
            .querySelectorAll(`input[name="${CSS.escape(SKU_AVAIL_PARAM)}"]`)
            .forEach((cb) => {
              const val = cb.value || '';
              activeAvail.forEach((type) => {
                if (hasAvailPrefix(val, type)) appendToken(val);
              });
            });
        }

        window.location.assign(`${url.pathname}?${params.toString()}`);
      } catch {
        // Fall back to default navigation
      }
    }
  }

  customElements.define('facet-remove', FacetRemove);
})();
